// src/components/CompetitionSchedule.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Collapse,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    TextField,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Add,
    Edit,
    Delete,
    ExpandMore,
    ExpandLess,
    CalendarToday,
    Schedule as ScheduleIcon,
    Person,
    Groups,
    Gavel
} from '@mui/icons-material';
import { getCompetitionSchedules, deleteSchedule, deleteHeat } from '../api/competitionApi';
import AddScheduleDialog from './AddScheduleDialog';
import AddHeatDialog from './AddHeatDialog';
import EditLaneDialog from './EditLaneDialog';

export default function CompetitionSchedule({ competition, isAdminView }) {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState('');
    const [expandedSchedule, setExpandedSchedule] = useState(null);
    const [expandedHeat, setExpandedHeat] = useState(null);
    const [openAddScheduleDialog, setOpenAddScheduleDialog] = useState(false);
    const [openAddHeatDialog, setOpenAddHeatDialog] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [editingHeat, setEditingHeat] = useState(null);
    const [selectedScheduleForHeat, setSelectedScheduleForHeat] = useState(null);
    const [openEditLaneDialog, setOpenEditLaneDialog] = useState(false);
    const [editingLane, setEditingLane] = useState(null);
    const [editingLaneHeat, setEditingLaneHeat] = useState(null);
    const [editingLaneSchedule, setEditingLaneSchedule] = useState(null);
    const [deleteScheduleConfirmOpen, setDeleteScheduleConfirmOpen] = useState(false);
    const [deleteHeatConfirmOpen, setDeleteHeatConfirmOpen] = useState(false);
    const [scheduleToDelete, setScheduleToDelete] = useState(null);
    const [heatToDelete, setHeatToDelete] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        loadSchedules();
    }, [competition.id]);

    const loadSchedules = async () => {
        try {
            setLoading(true);
            const data = await getCompetitionSchedules(competition.id);
            setSchedules(data);

            // Set initial date if available
            if (data.length > 0 && !selectedDate) {
                const dates = [...new Set(data.map(s => s.date))];
                setSelectedDate(dates[0]);
            }
        } catch (error) {
            console.error('Error loading schedules:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteScheduleClick = (scheduleId) => {
        setScheduleToDelete(scheduleId);
        setDeleteScheduleConfirmOpen(true);
    };

    const handleDeleteScheduleCancel = () => {
        setDeleteScheduleConfirmOpen(false);
        setScheduleToDelete(null);
    };

    const handleDeleteSchedule = async () => {
        try {
            setErrorMessage('');
            await deleteSchedule(scheduleToDelete);
            setDeleteScheduleConfirmOpen(false);
            setScheduleToDelete(null);
            loadSchedules();
        } catch (error) {
            console.error('Error deleting schedule:', error);
            setErrorMessage('Failed to delete schedule');
            setDeleteScheduleConfirmOpen(false);
        }
    };

    const handleDeleteHeatClick = (heatId) => {
        setHeatToDelete(heatId);
        setDeleteHeatConfirmOpen(true);
    };

    const handleDeleteHeatCancel = () => {
        setDeleteHeatConfirmOpen(false);
        setHeatToDelete(null);
    };

    const handleDeleteHeat = async () => {
        try {
            setErrorMessage('');
            await deleteHeat(heatToDelete);
            setDeleteHeatConfirmOpen(false);
            setHeatToDelete(null);
            loadSchedules();
        } catch (error) {
            console.error('Error deleting heat:', error);
            setErrorMessage('Failed to delete heat');
            setDeleteHeatConfirmOpen(false);
        }
    };

    const handleScheduleClick = (scheduleId) => {
        setExpandedSchedule(expandedSchedule === scheduleId ? null : scheduleId);
        setExpandedHeat(null);
    };

    const handleHeatClick = (heatId) => {
        setExpandedHeat(expandedHeat === heatId ? null : heatId);
    };

    const handleAddHeat = (schedule) => {
        setSelectedScheduleForHeat(schedule);
        setEditingHeat(null);
        setOpenAddHeatDialog(true);
    };

    const handleEditHeat = (heat, schedule, event) => {
        event.stopPropagation();
        setSelectedScheduleForHeat(schedule);
        setEditingHeat(heat);
        setOpenAddHeatDialog(true);
    };

    const handleEditLane = (lane, heat, schedule) => {
        setEditingLane({ ...lane, heatId: heat.id, competitionId: competition.id });
        setEditingLaneHeat(heat);
        setEditingLaneSchedule(schedule);
        setOpenEditLaneDialog(true);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('et-EE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const availableDates = [...new Set(schedules.map(s => s.date))].sort();
    const filteredSchedules = schedules.filter(s => s.date === selectedDate);

    const LaneItem = ({ lane, heat, schedule }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: { xs: 1.5, sm: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 0 },
                cursor: isAdminView ? 'pointer' : 'default',
                '&:hover': isAdminView ? {
                    bgcolor: 'action.hover'
                } : {}
            }}
            onClick={() => isAdminView && handleEditLane(lane, heat, schedule)}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                flex: 1,
                minWidth: 0
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                    flex: 1,
                    minWidth: 0
                }}>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                            minWidth: { xs: 'auto', sm: '50px' },
                            color: 'primary.main',
                            fontSize: { xs: '0.875rem', sm: '0.875rem' }
                        }}
                    >
                        Lane {lane.laneNumber}
                    </Typography>

                    {lane.time && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}>
                            <ScheduleIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.secondary' }} />
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                                {lane.time}
                            </Typography>
                        </Box>
                    )}

                    {lane.competitorName && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flex: { xs: '1 1 100%', sm: '0 1 auto' },
                            minWidth: 0,
                            maxWidth: { xs: '100%', sm: '200px' }
                        }}>
                            {competition.isTeamCompetition ? (
                                <Groups sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.main', flexShrink: 0 }} />
                            ) : (
                                <Person sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.main', flexShrink: 0 }} />
                            )}
                            <Typography
                                variant="body2"
                                fontWeight="500"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                            >
                                {lane.competitorName}
                            </Typography>
                        </Box>
                    )}

                    {lane.affiliate && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                        >
                            {lane.affiliate}
                        </Typography>
                    )}

                    {lane.division && (
                        <Typography
                            variant="caption"
                            sx={{
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                px: { xs: 0.75, sm: 1 },
                                py: 0.25,
                                borderRadius: 1,
                                fontWeight: 500,
                                fontSize: { xs: '0.65rem', sm: '0.75rem' }
                            }}
                        >
                            {lane.division}
                        </Typography>
                    )}

                    {lane.judgeName && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flex: { xs: '1 1 100%', sm: '0 1 auto' },
                            minWidth: 0,
                            maxWidth: { xs: '100%', sm: '180px' }
                        }}>
                            <Gavel sx={{ fontSize: { xs: 14, sm: 16 }, color: 'warning.main', flexShrink: 0 }} />
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    fontStyle: 'italic'
                                }}
                            >
                                Judge: {lane.judgeName}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    const HeatItem = ({ heat, schedule }) => {
        const isExpanded = expandedHeat === heat.id;

        return (
            <Paper
                sx={{
                    mb: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'action.hover' : 'background.paper',
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                    onClick={() => handleHeatClick(heat.id)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {heat.name}
                        </Typography>
                        {heat.time && (
                            <Typography variant="body2" color="text.secondary">
                                {heat.time}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isAdminView && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleEditHeat(heat, schedule, e)}
                                    color="primary"
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteHeatClick(heat.id);
                                    }}
                                    color="error"
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </>
                        )}
                        <IconButton size="small">
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={isExpanded}>
                    <Box sx={{ bgcolor: 'background.default' }}>
                        {heat.lanes && heat.lanes.length > 0 ? (
                            heat.lanes.map((lane) => (
                                <LaneItem key={lane.id} lane={lane} heat={heat} schedule={schedule} />
                            ))
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography color="text.secondary">
                                    No lanes configured
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        );
    };

    const ScheduleItem = ({ schedule }) => {
        const isExpanded = expandedSchedule === schedule.id;

        return (
            <Paper
                sx={{
                    mb: 2,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: isExpanded ? 'primary.main' : 'divider'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'primary.light' : 'background.paper',
                        '&:hover': {
                            bgcolor: isExpanded ? 'primary.light' : 'action.hover'
                        }
                    }}
                    onClick={() => handleScheduleClick(schedule.id)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarToday
                            sx={{
                                color: isExpanded ? 'primary.main' : 'text.secondary',
                                fontSize: 24
                            }}
                        />
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ color: isExpanded ? 'primary.main' : 'text.primary' }}
                        >
                            {schedule.name}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isAdminView && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingSchedule(schedule);
                                        setOpenAddScheduleDialog(true);
                                    }}
                                    color="primary"
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteScheduleClick(schedule.id);
                                    }}
                                    color="error"
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </>
                        )}
                        <IconButton size="small">
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={isExpanded}>
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default' }}>
                        {isAdminView && (
                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => handleAddHeat(schedule)}
                                sx={{ mb: 2 }}
                                fullWidth={isMobile}
                            >
                                Add Heat
                            </Button>
                        )}

                        {schedule.heats && schedule.heats.length > 0 ? (
                            schedule.heats.map((heat) => (
                                <HeatItem key={heat.id} heat={heat} schedule={schedule} />
                            ))
                        ) : (
                            <Paper sx={{ p: 3 }}>
                                <Typography align="center" color="text.secondary">
                                    No heats yet. {isAdminView && 'Click "Add Heat" to create one.'}
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        );
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading schedules...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}

            {/* Header with Date Selector */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    mb: 3,
                    p: { xs: 0, sm: 0 }
                }}
            >
                {availableDates.length > 0 && (
                    <FormControl sx={{ minWidth: { xs: '100%', sm: 250 } }}>
                        <Select
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            displayEmpty
                            sx={{
                                bgcolor: 'background.paper',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }
                            }}
                        >
                            {availableDates.map((date) => (
                                <MenuItem key={date} value={date}>
                                    <CalendarToday sx={{ fontSize: 18, mr: 1 }} />
                                    {formatDate(date)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {isAdminView && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => {
                            setEditingSchedule(null);
                            setOpenAddScheduleDialog(true);
                        }}
                        fullWidth={isMobile}
                    >
                        Add Schedule
                    </Button>
                )}
            </Box>

            {/* Schedule List */}
            <Box sx={{ px: { xs: 0, sm: 2 } }}>
                {filteredSchedules.length > 0 ? (
                    filteredSchedules.map((schedule) => (
                        <ScheduleItem key={schedule.id} schedule={schedule} />
                    ))
                ) : (
                    <Paper sx={{ p: 4 }}>
                        <Typography align="center" color="text.secondary">
                            {schedules.length === 0
                                ? `No schedules yet. ${isAdminView ? 'Click "Add Schedule" to create one.' : ''}`
                                : 'No schedules for selected date.'}
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* Dialogs */}
            <AddScheduleDialog
                open={openAddScheduleDialog}
                onClose={() => {
                    setOpenAddScheduleDialog(false);
                    setEditingSchedule(null);
                    loadSchedules();
                }}
                competitionId={competition.id}
                schedule={editingSchedule}
            />

            <AddHeatDialog
                open={openAddHeatDialog}
                onClose={() => {
                    setOpenAddHeatDialog(false);
                    setEditingHeat(null);
                    setSelectedScheduleForHeat(null);
                    loadSchedules();
                }}
                schedule={selectedScheduleForHeat}
                heat={editingHeat}
            />

            <EditLaneDialog
                open={openEditLaneDialog}
                onClose={() => {
                    setOpenEditLaneDialog(false);
                    setEditingLane(null);
                    setEditingLaneHeat(null);
                    setEditingLaneSchedule(null);
                    loadSchedules();
                }}
                lane={editingLane}
                heat={editingLaneHeat}
                schedule={editingLaneSchedule}
                isTeamCompetition={competition.isTeamCompetition}
            />

            {/* Delete Schedule Confirmation Dialog */}
            <Dialog
                open={deleteScheduleConfirmOpen}
                onClose={handleDeleteScheduleCancel}
                aria-labelledby="delete-schedule-dialog-title"
                aria-describedby="delete-schedule-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="delete-schedule-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="delete-schedule-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        Are you sure you want to delete this schedule? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleDeleteScheduleCancel}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSchedule}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Heat Confirmation Dialog */}
            <Dialog
                open={deleteHeatConfirmOpen}
                onClose={handleDeleteHeatCancel}
                aria-labelledby="delete-heat-dialog-title"
                aria-describedby="delete-heat-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="delete-heat-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="delete-heat-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        Are you sure you want to delete this heat? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleDeleteHeatCancel}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteHeat}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}